<template>
    <v-hover v-slot="{ hover }">
        <v-card :elevation="hover ? 6 : 2">
            <v-card-text>
                <v-row>
                    <v-col cols="auto">
                        <div class="text-left text-h5 font-weight-bold black--text">
                            <v-icon>mdi-chart-timeline-variant-shimmer</v-icon>Energy distribution by load groups
                        </div>
                        <div class="text-left text-subtitle-1">ปริมาณการใช้ไฟฟ้าตามประเภท</div>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                        เลือกช่วงเวลา
                        <DateRangePicker class="ml-2" v-model="range" :timePicker="false" />
                    </v-col>
                    <v-col cols="3">
                        <v-autocomplete placeholder="เลือกมิเตอร์" :disabled="loading" dense solo
                            v-model="selectedMeter" multiple small-chips deletable-chips
                            :items="assets" item-text="assetName" item-value="assetId"></v-autocomplete>
                    </v-col>
                    <v-col cols="auto"> <v-btn color="primary" :disabled="loading || !selectedMeter.length"
                            @click="getEnergy()">View</v-btn></v-col>
                </v-row>
                <div ref="chart" style="width: 100%;height:300px;"></div>
            </v-card-text>
        </v-card>
    </v-hover>
</template>
<script>
import * as echarts from 'echarts/core';
import {
    TitleComponent,
    TooltipComponent,
    LegendComponent
} from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import * as alasql from 'alasql'
import DateRangePicker from '@/components/common/DateRangePicker.vue'

echarts.use([
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    PieChart,
    CanvasRenderer,
    LabelLayout
]);

export default {
    props: {
        update: {
            type: Boolean
        }
    },
    computed: {
        building: {
            get: function () {
                return this.$store.state.User.building
            },
            set: function (newValue) {
                this.$store.dispatch('User/SET_BUILDING', newValue)
            }
        },
        assets() {
            return this.$store.state.Asset.assetEnergy.filter((el) => el.assetType === 'PowerMeter')
        },
        rootAssets() {
            return this.assets.filter((el) => el.root === true)
        },
        selectedAssets() {
            return this.assets.filter((el) => this.selectedMeter.includes(el.assetId))
        },
        energyValue() {
            return this.$store.state.Energy.monthlyEnergyCompare
        }
    },
    components: {
        DateRangePicker,
    },
    watch: {
        building() {
            this.initMeters()
        },
        assets(val) {
            if (val.length && !this.selectedMeter.length) {
                this.initMeters()
            }
        },
        update() {
            if (this.myChart) {
                this.myChart.resize()
            }
        },
    },
    data() {
        return {
            loading: false,
            range: {
                startDate: this.$dayjs().startOf('month').toDate(),
                endDate: this.$dayjs().endOf('month').toDate(),
            },
            myChart: null,
            selectedMeter: [],
        }
    },
    methods: {
        initMeters() {
            if (!this.rootAssets.length) {
                this.selectedMeter = []
                return
            }
            this.selectedMeter = this.rootAssets.map((a) => a.assetId)
            this.getEnergy()
        },
        formatKwh(value) {
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })
        },
        getEnergy() {
            if (!this.selectedMeter.length) return
            this.loading = true
            this.$store.dispatch('Energy/GET_MONTHLY_ENERGY', {
                BuildingId: this.building.BuildingId,
                type: 'monthly',
                assetId: this.selectedMeter,
                start: this.range.startDate.getTime(),
                end: this.range.endDate.getTime(),
                group: false,
            }).then((raw) => {
                const data = alasql(
                    `select P1.assetId, P1.assetName, SUM(P2.Unit) as Unit
                     from ? as P1 JOIN ? as P2 ON P1.assetId = P2.assetId
                     group by P1.assetId, P1.assetName`,
                    [this.selectedAssets, raw]
                )
                const totalKwh = data.reduce((sum, item) => sum + (Number(item.Unit) || 0), 0)
                const chartData = data.map((item) => ({
                    name: item.assetName,
                    value: Math.round(Number(item.Unit) || 0),
                }))
                this.myChart.setOption({
                    title: {
                        text: this.formatKwh(totalKwh),
                        subtext: 'kWh Total',
                        left: 'center',
                        top: 'center',
                        textStyle: { fontSize: 18, fontWeight: 'bold' },
                        subtextStyle: { fontSize: 12 },
                    },
                    tooltip: {
                        trigger: 'item',
                        formatter: (params) => {
                            return `${params.name}: ${this.formatKwh(params.value)} kWh (${params.percent}%)`
                        },
                    },
                    toolbox: {
                        feature: {
                            dataView: { show: true, readOnly: false },
                            restore: { show: true },
                            saveAsImage: { show: true },
                        },
                    },
                    legend: {
                        show: true,
                        orient: 'horizontal',
                        left: 'left',
                    },
                    series: [
                        {
                            type: 'pie',
                            radius: ['40%', '70%'],
                            name: 'kWh',
                            avoidLabelOverlap: true,
                            label: {
                                show: true,
                                formatter: (params) => {
                                    return `${params.name}\n${this.formatKwh(params.value)} kWh`
                                },
                            },
                            labelLine: { show: true },
                            data: chartData,
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                                },
                            },
                        },
                    ],
                })
                this.myChart.resize()
            }).catch(() => {
                // keep previous chart on error
            }).finally(() => {
                this.loading = false
            })
        },
    },
    mounted() {
        this.myChart = echarts.init(this.$refs.chart)
        const resizeObserver = new ResizeObserver(() => {
            this.myChart.resize()
        })
        resizeObserver.observe(this.$refs.chart)
        this.initMeters()
    },
}
</script>